/**
 * @fileoverview StyleGuide schemas for writing style configuration
 *
 * StyleGuides contain the core voice and writing rules:
 * - voice: Overall writing voice characteristics
 * - emojis: Approved emojis with usage context
 * - phrases:use: Phrases to incorporate
 * - phrases:avoid: Phrases to avoid
 * - formatting: Formatting rules and conventions
 */

import { z } from '@hono/zod-openapi';
import { createListResponseSchema } from './common.js';

/**
 * Valid style guide types
 */
export const STYLE_GUIDE_TYPES = ['voice', 'emojis', 'phrases:use', 'phrases:avoid', 'formatting'];

/**
 * Emoji usage frequency levels
 */
export const EMOJI_FREQUENCIES = ['rare', 'occasional', 'common'];

/**
 * Schema for approved emoji entries
 */
export const EmojiEntrySchema = z
  .object({
    emoji: z.string().openapi({
      description: 'The emoji character',
      example: '🤔',
    }),
    context: z.string().openapi({
      description: 'When to use this emoji',
      example: 'contemplation or questioning',
    }),
    frequency: z.enum(['rare', 'occasional', 'common']).openapi({
      description: 'How often this emoji should appear',
      example: 'common',
    }),
  })
  .openapi('EmojiEntry');

/**
 * Schema for emoji style guide content
 */
export const EmojiContentSchema = z
  .object({
    approved: z.array(EmojiEntrySchema).openapi({
      description: 'List of approved emojis with usage context',
    }),
    placementRules: z.array(z.string()).openapi({
      description: 'Rules for emoji placement in content',
      example: ['In headers: Section dividers only', 'In blurbs: End of sentence, max 1-2'],
    }),
    maxPerBlurb: z.number().int().min(0).max(5).openapi({
      description: 'Maximum emojis allowed per blurb',
      example: 2,
    }),
  })
  .openapi('EmojiContent');

/**
 * Schema for phrase entries (use or avoid)
 */
export const PhraseEntrySchema = z
  .object({
    phrase: z.string().openapi({
      description: 'The phrase text',
      example: "I've said this before, but...",
    }),
    context: z.string().optional().openapi({
      description: 'When/why to use or avoid this phrase',
      example: 'When reinforcing a recurring opinion',
    }),
    alternatives: z.array(z.string()).optional().openapi({
      description: 'Alternative phrases (for avoid entries)',
      example: ['previously', 'as mentioned'],
    }),
  })
  .openapi('PhraseEntry');

/**
 * Schema for phrases style guide content
 */
export const PhrasesContentSchema = z
  .object({
    phrases: z.array(PhraseEntrySchema).openapi({
      description: 'List of phrases with context',
    }),
    category: z.string().optional().openapi({
      description: 'Category of phrases (e.g., "agreement", "skepticism")',
      example: 'agreement',
    }),
  })
  .openapi('PhrasesContent');

/**
 * Schema for voice style guide content
 */
export const VoiceContentSchema = z
  .object({
    characteristics: z
      .array(
        z.object({
          trait: z.string().openapi({
            description: 'Voice trait name',
            example: 'Skeptical-but-enthusiastic',
          }),
          description: z.string().openapi({
            description: 'Description of the trait',
            example: "Believes in serverless but won't oversell it",
          }),
          examples: z.array(z.string()).optional().openapi({
            description: 'Example phrases demonstrating this trait',
          }),
        })
      )
      .openapi({
        description: 'Key voice characteristics',
      }),
    tone: z.string().openapi({
      description: 'Overall tone description',
      example: 'Professional but conversational',
    }),
    perspective: z.string().openapi({
      description: 'Narrative perspective',
      example: 'First person, uses "I" frequently',
    }),
  })
  .openapi('VoiceContent');

/**
 * Schema for formatting style guide content
 */
export const FormattingContentSchema = z
  .object({
    rules: z
      .array(
        z.object({
          rule: z.string().openapi({
            description: 'The formatting rule',
            example: 'Keep blurbs to 30-100 words',
          }),
          priority: z.enum(['required', 'recommended', 'optional']).openapi({
            description: 'How strictly to follow this rule',
            example: 'required',
          }),
          examples: z.array(z.string()).optional().openapi({
            description: 'Examples of correct formatting',
          }),
        })
      )
      .openapi({
        description: 'Formatting rules',
      }),
    structure: z
      .object({
        blurbFormula: z.array(z.string()).optional().openapi({
          description: 'Formula/structure for blurbs',
          example: ['Hook (optional, ~30%)', 'Core insight (always)', 'Personal angle (~70%)'],
        }),
        wordLimits: z
          .object({
            blurb: z
              .object({
                min: z.number().int(),
                max: z.number().int(),
              })
              .optional(),
            intro: z
              .object({
                min: z.number().int(),
                max: z.number().int(),
              })
              .optional(),
          })
          .optional()
          .openapi({
            description: 'Word count limits for different content types',
          }),
      })
      .optional()
      .openapi({
        description: 'Structural guidelines',
      }),
  })
  .openapi('FormattingContent');

/**
 * Union schema for style guide content based on type
 */
export const StyleGuideContentSchema = z.union([
  EmojiContentSchema,
  PhrasesContentSchema,
  VoiceContentSchema,
  FormattingContentSchema,
  z.record(z.unknown()), // Fallback for flexibility
]);

/**
 * Base StyleGuide schema
 */
const BaseStyleGuideSchema = z.object({
  id: z.string().openapi({
    description: 'Unique identifier for the style guide (typically matches type)',
    example: 'emojis',
  }),
  type: z.enum(['voice', 'emojis', 'phrases:use', 'phrases:avoid', 'formatting']).openapi({
    description: 'Type of style guide content',
    example: 'emojis',
  }),
  content: StyleGuideContentSchema.openapi({
    description: 'Style guide content (structure varies by type)',
  }),
  version: z.number().int().min(1).openapi({
    description: 'Version number for tracking changes',
    example: 1,
  }),
  created: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the style guide was created',
    example: '2025-01-15T10:00:00Z',
  }),
  modified: z.string().datetime().openapi({
    description: 'ISO 8601 timestamp when the style guide was last modified',
    example: '2025-01-15T14:30:00Z',
  }),
});

/**
 * Full StyleGuide schema
 */
export const StyleGuideSchema = BaseStyleGuideSchema.openapi('StyleGuide');

/**
 * Create schema - omit auto-generated fields
 */
export const CreateStyleGuideSchema = StyleGuideSchema.omit({
  created: true,
  modified: true,
})
  .extend({
    version: z.number().int().min(1).optional().default(1).openapi({
      description: 'Version number (defaults to 1)',
      example: 1,
    }),
  })
  .openapi('CreateStyleGuide');

/**
 * Update schema - partial, omit immutable fields
 */
export const UpdateStyleGuideSchema = BaseStyleGuideSchema.partial()
  .omit({
    id: true,
    created: true,
    modified: true,
  })
  .openapi('UpdateStyleGuide');

export const StyleGuideResponseSchema = StyleGuideSchema.openapi('StyleGuideResponse');

export const StyleGuideListResponseSchema = createListResponseSchema(
  StyleGuideResponseSchema,
  'styleGuides',
  'StyleGuideListResponse'
);

/**
 * @typedef {z.infer<typeof StyleGuideSchema>} StyleGuideType
 */
export const StyleGuideType = /** @type {StyleGuideType} */ ({});
